import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { ArrowRight, Clock, PoundSterling } from "lucide-react";
import { getCourseUrl } from "@/lib/courseSlug";

// Maps blog categories to course categories for contextual cross-linking
const CATEGORY_MAP = {
  first_aid_tips: ["first_aid"],
  safety_advice: ["health_safety", "fire_safety", "food_safety", "manual_handling", "iosh"],
  training_updates: ["health_safety", "first_aid"],
  health_news: ["mental_health", "safeguarding"],
  workplace_safety: ["health_safety", "manual_handling", "iosh"],
};

const FALLBACK_IMG = "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop";

export default function RelatedCourses({ blogCategory }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const targetCats = CATEGORY_MAP[blogCategory] || [];
        if (targetCats.length === 0) {
          if (alive) setLoading(false);
          return;
        }

        // Fetch courses matching the mapped categories
        const all = await base44.entities.Course.filter({ is_visible: true }, "sort_order", 50);
        if (!alive) return;

        const matched = all.filter(c => targetCats.includes(c.category)).slice(0, 3);
        setCourses(matched);
      } catch (e) {
        console.error("Error loading related courses:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [blogCategory]);

  if (loading || courses.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 mb-8 border-l-4 border-brand">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Related Training Courses</h3>
        <Link to="/shop" className="text-sm font-semibold text-brand hover:text-brand-700 transition-colors flex items-center gap-1">
          Browse all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <p className="text-gray-600 mb-5 text-sm">
        Accredited courses relevant to this article — book online or contact us for group training.
      </p>
      <div className="grid sm:grid-cols-3 gap-4">
        {courses.map(course => (
          <Link
            key={course.id}
            to={getCourseUrl(course)}
            className="group border border-hairline rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="relative h-28 overflow-hidden">
              <img
                src={course.image_url || FALLBACK_IMG}
                alt={course.image_alt || `${course.title} training course`}
                width={300} height={150}
                loading="lazy" decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-4">
              <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-2 group-hover:text-brand transition-colors">
                {course.title}
              </h4>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {course.duration}
                </span>
                {course.price > 0 && (
                  <span className="flex items-center gap-0.5 font-semibold text-brand">
                    <PoundSterling className="w-3 h-3" />{Number(course.price).toFixed(0)}
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}